import {Button,Box} from '@mui/material'
import styled from 'styled-components'
import {useNavigate} from 'react-router-dom'
import {Form as FormikForm, Formik} from 'formik'
import debounce from 'lodash/debounce'
import React, {useCallback, useMemo} from 'react'
import * as Yup from 'yup'
import DowntownBg from '~/src/assets/images/downtown-bg.jpeg'
import DowntownPa from '~/src/assets/images/Paris.jpg'
import {SIDEBAR_ROUTES} from '../../routes'
import {useCreateAgencyMutation} from '../../store/apis/agency-api'
import {useAppDispatch} from '../../store/store-hooks'
import {useLazyGetUserByEmailQuery} from '~/src/store/apis/user-api'
import {AgencyForm, type AgencyFormValues} from '../../components/platform/agency/AgencyForm'

export type TActivePlatform = 'Company' | 'Agency'

const Outer = styled.div`
  width: 100%;
  height: 100vh;
  flex: 1;
  overflow: hidden;
`
const Background = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  background: linear-gradient(rgba(35, 35, 35, 0.64), rgba(35, 35, 35, 0.64)),
    url(${DowntownBg}) center 40% no-repeat;
  background-size: 135%;
`

const BackgroundPa = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  background: linear-gradient(rgba(35, 35, 35, 0.64), rgba(35, 35, 35, 0.64)),
    url(${DowntownPa}) center 40% no-repeat;
  background-size: 135%;
`

const MaxWidth = styled.div`
  overflow-y: auto;
  left: 50%;
  max-width: 90%;
  max-height: 90%;
  padding: 30px 9.866%;
  position: relative;
  top: 50%;
  transform: translate(-50%, -50%);

  @media (min-width: 1200px) {
    padding: 0;
  }
`

const Header = styled.div`
  width: 100%;
  border-bottom: 1px solid #cdced9;
`

const HeaderText = styled.div`
  font-size: 28px;
  margin: 25px;
  margin-left: 50px;
  line-height: 32px;
  font-weight: 700;
`

interface IProps {
  activePlatform: TActivePlatform
}

export default function CreateAgency({activePlatform}: IProps) {
  const [addAgency, {error}] = useCreateAgencyMutation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const initialValues: AgencyFormValues = {
    name: '',
    phoneNumber: '',
    extension: '',
    website: '',
    address: {
      line1: '',
      city: '',
      state: '',
      zipCode: '',
      display: '',
    },
    billingAddress: {
      line1: '',
      city: '',
      state: '',
      zipCode: '',
      display: '',
    },
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    isMsp: false,
    sameAsAgencyAddress: false,
  }

  const handleSubmit = async (values: AgencyFormValues) => {
    try {
      const agencyData = {
        name: values.name,
        phoneNumber: values.phoneNumber,
        extension: values.extension,
        website: values.website || null,
        address: values.address,
        billingAddress: values.sameAsAgencyAddress ? values.address : values.billingAddress,
        adminFirstName: values.adminFirstName,
        adminLastName: values.adminLastName,
        adminEmail: values.adminEmail,
        isMsp: values.isMsp,
      }

      const result = await addAgency(agencyData).unwrap()
      // Navigate to the agency detail page on success
      navigate(`${SIDEBAR_ROUTES.MANAGE_AGENCIES}/${result.id}`)
    } catch (err) {
      console.error('Failed to create agency:', err)
    }
  }

  const [findEmail] = useLazyGetUserByEmailQuery()

  const validateEmail = useCallback(
    async function (value: string | undefined, resolve: (value: boolean) => void) {
      try {
        const alreadyExists = await findEmail(value ?? '').unwrap()
        resolve(!alreadyExists)
      } catch (e: unknown) {
        resolve(true)
      }
    },
    [findEmail],
  )

  const debouncedValidateEmail = useMemo(
    function () {
      return debounce(validateEmail, 500)
    },
    [validateEmail],
  )

  return (
    <Outer>
      {import.meta.env.VITE_FR ? <BackgroundPa /> : <Background />}
      <MaxWidth>
        <>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: '#FFFFFF',
              border: '1px solid #CDCED9',
              borderRadius: '8px',
            }}>
            <Header>
              <HeaderText>Registration</HeaderText>
            </Header>

            <Formik
              onSubmit={handleSubmit}
              initialValues={initialValues}
              validationSchema={Yup.object({
                name: Yup.string().required('Required'),
                phoneNumber: Yup.string()
                  .required('Required')
                  .phone('US', true, 'Phone number is invalid.'),
                extension: Yup.string(),
                website: Yup.string(),
                adminEmail: Yup.string()
                  .test(
                    'already-exists',
                    'This email is already in use',
                    (value) => new Promise((resolve) => debouncedValidateEmail(value, resolve)),
                  )
                  .email('Email address is invalid.')
                  .required('Required'),
                adminFirstName: Yup.string().required('Required'),
                adminLastName: Yup.string().required('Required'),
                isMsp: Yup.boolean().required('Required'),
                sameAsAgencyAddress: Yup.boolean(),
                address: Yup.object({
                  line1: Yup.string(),
                  city: Yup.string(),
                  state: Yup.string(),
                  zipCode: Yup.string(),
                  display: Yup.string().required('Required'),
                }),
                billingAddress: Yup.object({
                  line1: Yup.string(),
                  city: Yup.string(),
                  state: Yup.string(),
                  zipCode: Yup.string(),
                  display: Yup.string().when('sameAsAgencyAddress', {
                    is: false,
                    then: (schema) => schema.required('Required'),
                    otherwise: (schema) => schema,
                  }),
                }),
              })}>
              {({isSubmitting}) => {
                return (
                  <FormikForm>
                    <Box style={{margin: 50}}>
                      <AgencyForm />

                      <Box mt={6} display="flex">
                        <Button
                          variant="outlined"
                          color="secondary"
                          disabled={isSubmitting}
                          onClick={() => {
                            navigate(BASE_ROUTES.SIGNIN)
                          }}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                          sx={{ml: 2}}>
                          Save
                        </Button>
                      </Box>
                    </Box>
                  </FormikForm>
                )
              }}
            </Formik>
          </Box>
        </>
      </MaxWidth>
    </Outer>
  )
}
